import { useState, useEffect, useCallback } from "react";
import { DropdownEnum } from "enum/DropdownEnum";
import {
  getIncidentStatuses,
  getWhsDropdownOptions,
  addWhsDropdownOption,
  editWhsDropdownOption,
  deleteWhsDropdownOption,
} from "services/whs/whsService";
import { getInternalUsersForAssigningLeads, getAllUsers } from "services/userService";
import { toast } from "sonner";

export function useIncidentDropdowns({
  companyId,
  locationState,
  loggedInUser,
  reportedBy,
  type,
  setType,
  severity,
  setSeverity,
  status,
  setStatus,
}) {
  const [typeOptions, setTypeOptions] = useState([]);
  const [severityOptions, setSeverityOptions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [internalUserOptions, setInternalUserOptions] = useState([]);
  const [allUserOptions, setAllUserOptions] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // Fetch all dropdown options and user lists when companyId is resolved
  useEffect(() => {
    if (!companyId) return;
    let isMounted = true;

    // 1. Fetch dropdown options (IncidentType & IncidentSeverity) via /api/WHS/DropdownOptions
    const fetchDropdowns = async () => {
      setLoadingDropdowns(true);
      try {
        const [typeRes, sevRes] = await Promise.allSettled([
          getWhsDropdownOptions(companyId, DropdownEnum.IncidentType),
          getWhsDropdownOptions(companyId, DropdownEnum.IncidentSeverity),
        ]);

        if (typeRes.status === "fulfilled" && typeRes.value?.success && isMounted) {
          setTypeOptions(
            (typeRes.value.data || []).map((opt) => ({
              value: opt.id || opt.Id,
              label: opt.optionName || opt.OptionName,
            }))
          );
        }

        if (sevRes.status === "fulfilled" && sevRes.value?.success && isMounted) {
          setSeverityOptions(
            (sevRes.value.data || []).map((opt) => ({
              value: opt.id || opt.Id,
              label: opt.optionName || opt.OptionName,
            }))
          );
        }
      } catch (err) {
        console.error("Failed to load WHS dropdown options:", err);
      } finally {
        if (isMounted) setLoadingDropdowns(false);
      }
    };

    // 2. Fetch statuses via /api/WHS/IncidentStatuses
    const fetchStatuses = async () => {
      try {
        const res = await getIncidentStatuses(companyId);
        let rawData = res?.data !== undefined ? res.data : res;
        if (typeof rawData === "string") {
          try {
            rawData = JSON.parse(rawData);
          } catch (e) {
            console.error("Failed to parse statuses JSON string:", e);
          }
        }
        if (Array.isArray(rawData) && isMounted) {
          const mapped = rawData.map((st) => ({
            label: st.Name || st.StatusName || st.name || st.statusName || "Unnamed Status",
            value: st.Id || st.id || st.StatusId || st.statusId,
          }));
          setStatuses(mapped);

          // Default to Open status if not set
          if (!status && mapped.length > 0) {
            const openStatus = mapped.find((s) => s.label?.toLowerCase() === "open") || mapped[0];
            setStatus(openStatus.value);
          }
        }
      } catch (err) {
        console.error("Error fetching incident statuses:", err);
      }
    };

    // 3. Fetch users:
    const fetchUsers = async () => {
      try {
        const [internalRes, allRes] = await Promise.allSettled([
          getInternalUsersForAssigningLeads(companyId),
          getAllUsers({ companyId, pageSize: 500 }),
        ]);

        if (
          internalRes.status === "fulfilled" &&
          internalRes.value?.success &&
          internalRes.value?.data &&
          isMounted
        ) {
          const rawUsers = Array.isArray(internalRes.value.data)
            ? internalRes.value.data
            : typeof internalRes.value.data === "string"
            ? JSON.parse(internalRes.value.data)
            : [];
          const mappedInternal = rawUsers.map((u) => ({
            label: u.fullName || u.label || u.FullName || u.name || u.email,
            value: u.fullName || u.label || u.FullName || u.name || u.email,
            userId: u.userId || u.UserId || u.id || u.Id,
            designation: u.designationName || u.designations || u.DesignationName,
          }));
          setInternalUserOptions(mappedInternal);
        }

        if (allRes.status === "fulfilled" && allRes.value?.data && isMounted) {
          const rawAll = Array.isArray(allRes.value.data)
            ? allRes.value.data
            : typeof allRes.value.data === "string"
            ? JSON.parse(allRes.value.data)
            : [];
          const mappedAll = rawAll
            .map((u) => {
              const userId = u.userId ?? u.UserId ?? u.id ?? u.Id ?? u.UserID;
              const label = u.name || u.FullName || u.fullName || u.email;
              if (!userId || !label) return null;
              return {
                label: String(label),
                value: String(userId),
                userId: String(userId),
                designation:
                  u.designationName || u.DesignationName || u.role || u.RoleName || u.roleName,
              };
            })
            .filter(Boolean);
          setAllUserOptions(mappedAll);
        }
      } catch (err) {
        console.error("Error fetching users for incident report:", err);
      }
    };

    fetchDropdowns();
    fetchStatuses();
    fetchUsers();

    return () => {
      isMounted = false;
    };
  }, [companyId]);

  // Helper to resolve an effective User ID GUID for adding options
  const getEffectiveUserId = useCallback(() => {
    const fromState = locationState?.userId || locationState?.userForm?.userId;
    if (fromState) return fromState;
    const fromStorage = localStorage.getItem("whs_user_id");
    if (fromStorage) return fromStorage;
    const fromAuth = loggedInUser?.id || loggedInUser?.userId;
    if (fromAuth) return fromAuth;
    const matchedUser = internalUserOptions.find(
      (u) =>
        u.value === reportedBy ||
        u.label === reportedBy ||
        u.userId === reportedBy
    );
    if (matchedUser?.userId) return matchedUser.userId;
    if (internalUserOptions.length > 0 && internalUserOptions[0]?.userId) {
      return internalUserOptions[0].userId;
    }
    return null;
  }, [locationState, loggedInUser, reportedBy, internalUserOptions]);

  // Refreshes dropdown options from backend to ensure consistent state
  const refreshDropdownOptions = useCallback(
    async (enumValue, setOptions) => {
      if (!companyId) return;
      try {
        const res = await getWhsDropdownOptions(companyId, enumValue);
        if (res?.success) {
          setOptions(
            (res.data || []).map((opt) => ({
              value: opt.id || opt.Id,
              label: opt.optionName || opt.OptionName,
            }))
          );
        }
      } catch (err) {
        console.error("Error refreshing dropdown options:", err);
      }
    },
    [companyId]
  );

  // Dropdown CRUD handlers for Incident Type (DropdownEnum.IncidentType = 1001)
  const handleCreateIncidentType = useCallback(
    async (name) => {
      if (!name?.trim()) return { success: false };
      if (!companyId) {
        toast.error("Company context is missing.");
        return { success: false };
      }
      const effectiveUserId = getEffectiveUserId();
      if (!effectiveUserId) {
        toast.error("User context is required to add an option. Please ensure you are checked in.");
        return { success: false };
      }

      try {
        const res = await addWhsDropdownOption({
          companyId,
          userId: effectiveUserId,
          enumValue: DropdownEnum.IncidentType,
          optionName: name.trim(),
        });

        if (res?.success && res?.optionId) {
          toast.success("Incident type added successfully");
          const newOpt = { value: res.optionId, label: name.trim() };
          setTypeOptions((prev) => [...prev, newOpt]);
          setType(res.optionId);
          refreshDropdownOptions(DropdownEnum.IncidentType, setTypeOptions);
          return { success: true, optionId: res.optionId };
        } else {
          toast.error(res?.error || res?.message || "Failed to add incident type");
          return { success: false };
        }
      } catch (err) {
        console.error("Error adding incident type:", err);
        toast.error("Failed to add incident type");
        return { success: false };
      }
    },
    [companyId, getEffectiveUserId, refreshDropdownOptions, setType]
  );

  const handleEditIncidentType = useCallback(
    async (id, currentLabel, newText) => {
      if (!newText?.trim() || newText.trim() === currentLabel?.trim()) return false;
      if (!companyId) {
        toast.error("Company context is missing.");
        return false;
      }

      try {
        const res = await editWhsDropdownOption({
          optionId: id,
          companyId,
          optionName: newText.trim(),
        });

        if (res?.success) {
          toast.success("Incident type updated successfully");
          setTypeOptions((prev) =>
            prev.map((opt) =>
              String(opt.value) === String(id) ? { ...opt, label: newText.trim() } : opt
            )
          );
          refreshDropdownOptions(DropdownEnum.IncidentType, setTypeOptions);
          return true;
        } else {
          toast.error(res?.error || res?.message || "Failed to update incident type");
          return false;
        }
      } catch (err) {
        console.error("Error editing incident type:", err);
        toast.error("Failed to update incident type");
        return false;
      }
    },
    [companyId, refreshDropdownOptions]
  );

  const handleDeleteIncidentType = useCallback(
    async (id) => {
      if (!companyId) {
        toast.error("Company context is missing.");
        return false;
      }

      try {
        const res = await deleteWhsDropdownOption({
          optionId: id,
          companyId,
        });

        if (res?.success) {
          toast.success("Incident type deleted successfully");
          setTypeOptions((prev) => prev.filter((opt) => String(opt.value) !== String(id)));
          if (String(type)?.toLowerCase() === String(id)?.toLowerCase()) {
            setType("");
          }
          refreshDropdownOptions(DropdownEnum.IncidentType, setTypeOptions);
          return true;
        } else {
          toast.error(res?.error || res?.message || "Failed to delete incident type");
          return false;
        }
      } catch (err) {
        console.error("Error deleting incident type:", err);
        toast.error("Failed to delete incident type");
        return false;
      }
    },
    [companyId, type, setType, refreshDropdownOptions]
  );

  // Dropdown CRUD handlers for Incident Severity (DropdownEnum.IncidentSeverity = 1002)
  const handleCreateSeverity = useCallback(
    async (name) => {
      if (!name?.trim()) return { success: false };
      if (!companyId) {
        toast.error("Company context is missing.");
        return { success: false };
      }
      const effectiveUserId = getEffectiveUserId();
      if (!effectiveUserId) {
        toast.error("User context is required to add an option. Please ensure you are checked in.");
        return { success: false };
      }

      try {
        const res = await addWhsDropdownOption({
          companyId,
          userId: effectiveUserId,
          enumValue: DropdownEnum.IncidentSeverity,
          optionName: name.trim(),
        });

        if (res?.success && res?.optionId) {
          toast.success("Severity level added successfully");
          const newOpt = { value: res.optionId, label: name.trim() };
          setSeverityOptions((prev) => [...prev, newOpt]);
          setSeverity(res.optionId);
          refreshDropdownOptions(DropdownEnum.IncidentSeverity, setSeverityOptions);
          return { success: true, optionId: res.optionId };
        } else {
          toast.error(res?.error || res?.message || "Failed to add severity level");
          return { success: false };
        }
      } catch (err) {
        console.error("Error adding severity level:", err);
        toast.error("Failed to add severity level");
        return { success: false };
      }
    },
    [companyId, getEffectiveUserId, refreshDropdownOptions, setSeverity]
  );

  const handleEditSeverity = useCallback(
    async (id, currentLabel, newText) => {
      if (!newText?.trim() || newText.trim() === currentLabel?.trim()) return false;
      if (!companyId) {
        toast.error("Company context is missing.");
        return false;
      }

      try {
        const res = await editWhsDropdownOption({
          optionId: id,
          companyId,
          optionName: newText.trim(),
        });

        if (res?.success) {
          toast.success("Severity level updated successfully");
          setSeverityOptions((prev) =>
            prev.map((opt) =>
              String(opt.value) === String(id) ? { ...opt, label: newText.trim() } : opt
            )
          );
          refreshDropdownOptions(DropdownEnum.IncidentSeverity, setSeverityOptions);
          return true;
        } else {
          toast.error(res?.error || res?.message || "Failed to update severity level");
          return false;
        }
      } catch (err) {
        console.error("Error editing severity level:", err);
        toast.error("Failed to update severity level");
        return false;
      }
    },
    [companyId, refreshDropdownOptions]
  );

  const handleDeleteSeverity = useCallback(
    async (id) => {
      if (!companyId) {
        toast.error("Company context is missing.");
        return false;
      }

      try {
        const res = await deleteWhsDropdownOption({
          optionId: id,
          companyId,
        });

        if (res?.success) {
          toast.success("Severity level deleted successfully");
          setSeverityOptions((prev) => prev.filter((opt) => String(opt.value) !== String(id)));
          if (String(severity)?.toLowerCase() === String(id)?.toLowerCase()) {
            setSeverity("");
          }
          refreshDropdownOptions(DropdownEnum.IncidentSeverity, setSeverityOptions);
          return true;
        } else {
          toast.error(res?.error || res?.message || "Failed to delete severity level");
          return false;
        }
      } catch (err) {
        console.error("Error deleting severity level:", err);
        toast.error("Failed to delete severity level");
        return false;
      }
    },
    [companyId, severity, setSeverity, refreshDropdownOptions]
  );

  return {
    typeOptions,
    severityOptions,
    statuses,
    internalUserOptions,
    allUserOptions,
    loadingDropdowns,
    handleCreateIncidentType,
    handleEditIncidentType,
    handleDeleteIncidentType,
    handleCreateSeverity,
    handleEditSeverity,
    handleDeleteSeverity,
  };
}

export default useIncidentDropdowns;
